import React from "react";
import { useNavigate } from "react-router-dom";
import type { serviceStruct } from "../interfaces/serviceInterface";
import { Box, Typography, Chip, Card } from "@mui/material";
import { Smartphone, Wifi, ChevronRight, MapPin, Calendar, AlertTriangle } from "lucide-react";
import { calculateRemainingDays } from "@/tools/reamingDays";
import { formatCurrency } from "@/tools/formatCurrency";

type Props = {
  service: serviceStruct;
  selected?: boolean;
  onClick?: () => void;
};

function getServiceIcon(type?: string) {
  if (!type) return <Smartphone size={24} />;
  const t = type.toLowerCase();
  if (t.includes("internet") || t.includes("inet")) return <Wifi size={24} />;
  return <Smartphone size={24} />;
}

export default function ServiceCard({ service, selected = false, onClick }: Props) {
  const navigate = useNavigate();
  const title = service.planInfo?.planName || "Servicio sin nombre";
  const planData = service.plan[0] ?? {}
  const description = planData.description ?? "";
  const servicePrice = planData.price;
  const serviceAddress = service.serviceInstalledAddress ?? "-";
  const status = service.serviceState ?? service.paymentStatus ?? "Activo";
  const statusColor = status.toLowerCase().includes("activo") ? "hsl(142 71.8% 29.2%)" : "hsl(0 84.2% 60.2%)";
  const isPospago = planData.serviceType?.toLowerCase().includes("pospago") ?? false;
  const planName = planData.name ?? "-";
  const downloadSpeed = planData.downloadSpeed ?? null;
  const uploadSpeed = planData.uploadSpeed ?? null;
  const period = planData.period ?? 30;
  const planTax = planData.tax ?? 0;
  const planSubtotal = servicePrice ?? 0;
  const lastInvoice = service.planInvoice?.[0];

  const expirationInfo = lastInvoice?.expiresAt ? {
    daysRemaining: calculateRemainingDays(lastInvoice.expiresAt),
    expiresAt: lastInvoice.expiresAt,
    isExpired: calculateRemainingDays(lastInvoice.expiresAt) < 0,
    isUrgent: calculateRemainingDays(lastInvoice.expiresAt) >= 0 && calculateRemainingDays(lastInvoice.expiresAt) <= 1,
    isWarning: calculateRemainingDays(lastInvoice.expiresAt) > 1 && calculateRemainingDays(lastInvoice.expiresAt) <= 7,
  } : null;

  const handleCardClick = () => {
    const serviceId = String(service._id ?? service.documentNumber ?? "0");
    navigate(`/dashboard/service/${serviceId}`);
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        p: { xs: 2.5, sm: 3 },
        bgcolor: "hsl(0 0% 100%)",
        border: "1px solid hsl(240 4.8% 95.9%)",
        borderRadius: "16px",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
        minHeight: "100%",
        "&:hover": {
          boxShadow: "0 12px 24px rgba(0, 0, 0, 0.12)",
          borderColor: "hsl(224.3 76.3% 48%)",
          transform: "translateY(-4px)",
        },
        "&:active": {
          transform: "scale(0.99)",
        },
      }}
      role="button"
      tabIndex={0}
    >
      {/* Header with Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            color: "hsl(224.3 76.3% 48%)",
            display: "flex",
            minWidth: "32px",
            height: "32px",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "hsl(224.3 76.3% 95%)",
            borderRadius: "10px",
          }}
        >
          {getServiceIcon("inet")}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {status && (
            <Chip
              label={status}
              size="small"
              sx={{
                bgcolor: statusColor,
                color: "white",
                fontWeight: 600,
                fontSize: "0.7rem",
                height: "24px",
              }}
            />
          )}
          {service.serviceType && (
            <Chip
              label={service.serviceType}
              size="small"
              sx={{
                bgcolor: "hsl(224.3 76.3% 48%)",
                color: "white",
                fontWeight: 600,
                fontSize: "0.7rem",
                height: "24px",
              }}
            />
          )}
        </Box>
      </Box>

      {/* Service Name */}
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: "hsl(240 10% 3.9%)",
          mb: 0.5,
          fontSize: { xs: "1rem", sm: "1.15rem" },
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>

      {/* Description */}
      {description && (
        <Typography
          variant="caption"
          sx={{
            color: "hsl(240 3.8% 46.1%)",
            fontSize: "0.85rem",
            mb: 2,
            lineHeight: 1.4,
          }}
        >
          {description}
        </Typography>
      )}

      {/* Address */}
      {serviceAddress && serviceAddress !== "-" && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5, pb: 2.5, borderBottom: "1px solid hsl(240 4.8% 95.9%)" }}>
          <MapPin size={16} style={{ color: "hsl(224.3 76.3% 48%)", marginTop: "2px", flexShrink: 0, opacity: 0.7 }} />
          <Typography
            variant="caption"
            sx={{
              color: "hsl(240 3.8% 46.1%)",
              fontSize: "0.82rem",
              lineHeight: 1.4,
            }}
          >
           {service.neighborhood} •  {service.serviceInstalledAddress} 
          </Typography>
        </Box>
      )}

      {/* Plan Info for Pospago */}
      {isPospago && (
        <Box sx={{ mb: 2.5 }}>
          {planName && planName !== "-" && (
            <>
              <Typography
                variant="caption"
                sx={{
                  color: "hsl(240 3.8% 46.1%)",
                  display: "block",
                  mb: 0.5,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Plan
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "hsl(240 10% 3.9%)",
                  mb: 1.5,
                  fontSize: "0.95rem",
                }}
              >
                {planName}
              </Typography>
            </>
          )}

          <Typography
            variant="caption"
            sx={{
              color: "hsl(240 3.8% 46.1%)",
              display: "block",
              mb: 0.5,
              fontSize: "0.7rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Tarifa mensual
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "hsl(224.3 76.3% 48%)",
              fontSize: { xs: "1.4rem", sm: "1.6rem" },
              mb: 1.5,
              letterSpacing: "-0.5px",
            }}
          >
            {formatCurrency(servicePrice)}
          </Typography>

          <Box sx={{ fontSize: "0.8rem", color: "hsl(240 3.8% 46.1%)", p: 1.5, backgroundColor: "hsl(224.3 76.3% 97%)", borderRadius: "10px", mb: 0 }}>
            
              <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "0.75rem" }}>IVA </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "hsl(240 10% 3.9%)", fontSize: "0.75rem" }}>{formatCurrency(planTax)}</Typography>
            
          </Box>
        </Box>
      )}

      {/* Plan Info for Prepago */}
      {!isPospago && (
        <Box sx={{ mb: 2.5, p: 2, backgroundColor: "hsl(224.3 76.3% 97%)", borderRadius: "12px", borderLeft: "4px solid hsl(224.3 76.3% 48%)" }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "hsl(224.3 76.3% 48%)",
              fontSize: "0.9rem",
              lineHeight: 1.4,
            }}
          >
            Ver detalles de tu servicio
          </Typography>
        </Box>
      )}

      {/* Expiration Date */}
      {expirationInfo && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 0,
            p: 1.5,
            backgroundColor: expirationInfo.isUrgent
              ? "hsl(0 84.2% 97%)"
              : expirationInfo.isWarning
                ? "hsl(34.3 100% 97%)"
                : "hsl(224.3 76.3% 97%)",
            borderRadius: "12px",
            border: `1px solid ${expirationInfo.isUrgent
                ? "hsl(0 84.2% 80%)"
                : expirationInfo.isWarning
                  ? "hsl(34.3 97.3% 80%)"
                  : "hsl(224.3 76.3% 80%)"
              }`
          }}
        >
          <Calendar
            size={16}
            style={{
              color: expirationInfo.isUrgent
                ? "hsl(0 84.2% 50%)"
                : expirationInfo.isWarning
                  ? "hsl(34.3 97.3% 50%)"
                  : "hsl(224.3 76.3% 48%)",
              flexShrink: 0,
              opacity: 0.8
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: expirationInfo.isUrgent
                  ? "hsl(0 84.2% 40%)"
                  : expirationInfo.isWarning
                    ? "hsl(34.3 97.3% 40%)"
                    : "hsl(224.3 76.3% 40%)",
                display: "block",
                fontSize: "0.7rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {expirationInfo.isExpired
                ? "Vencido"
                : expirationInfo.isUrgent
                  ? "Vence hoy"
                  : "Vencimiento"}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: expirationInfo.isUrgent
                  ? "hsl(0 84.2% 50%)"
                  : expirationInfo.isWarning
                    ? "hsl(34.3 97.3% 50%)"
                    : "hsl(224.3 76.3% 48%)",
                fontSize: "0.85rem",
              }}
            >
              {expirationInfo.isExpired
                ? "Requiere renovación"
                : expirationInfo.isUrgent
                  ? "Acción requerida"
                  : `${new Date(expirationInfo.expiresAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })} - ${expirationInfo.daysRemaining} día${expirationInfo.daysRemaining !== 1 ? 's' : ''}`}
            </Typography>
          </Box>
        </Box>
      )}

    </Card>
  );
}
